/**
 * §1.2.1 — the Operational Health Widget.
 *
 * A single 100%-width stacked bar whose segments are proportional to project
 * count, pre-filtered to `active_fieldwork` on load, with a chip row below it
 * for direct filtering. Clicking a segment filters; clicking the active
 * segment again clears the filter.
 *
 * Two honesty rules the widget will not bend:
 *  - A segment with no projects is not drawn in the bar (it has no width) but
 *    IS listed in the chip row with a zero, so the reader can tell "no project
 *    is on hold" from "on hold was not measured".
 *  - Aggregate contract value renders as an em dash with a stated reason
 *    whenever the segment's projects do not all carry a financial row. It is
 *    never rendered as $0.
 */
import { useEffect, useRef, useState } from 'react';
import type { HealthSegment, OperationalHealth as HealthReport, PillarCode } from '../lib/api';
import { api } from '../lib/api';
import { money, segmentPaint, statusLabel } from '../lib/status';

interface Props {
  scope: PillarCode | null;
  selected: string | null;
  onSelect: (status: string | null) => void;
}

/** §1.2.1: "Refresh: 60-second polling." */
const POLL_MS = 60_000;

export function OperationalHealthWidget({ scope, selected, onSelect }: Props): JSX.Element {
  const [report, setReport] = useState<HealthReport | null>(null);
  const [failed, setFailed] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  // The default is applied once per scope, not on every poll: re-applying it
  // every sixty seconds would yank the filter out from under whoever is
  // reading a different segment.
  const defaultApplied = useRef<string | null>(null);

  useEffect(() => {
    let live = true;
    const load = async (): Promise<void> => {
      try {
        const next = await api.operationalHealth(scope);
        if (!live) return;
        setReport(next);
        setFailed(false);
        const key = scope ?? 'all';
        if (defaultApplied.current !== key) {
          defaultApplied.current = key;
          onSelect(next.defaultSegment);
        }
      } catch {
        if (live) setFailed(true);
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [scope, onSelect]);

  if (failed) {
    return (
      <section className="card p-4" aria-labelledby="health-heading">
        <h2 id="health-heading" className="text-h2 font-semibold">
          Operational health
        </h2>
        <p role="status" className="mt-2 text-body text-warning">
          Operational health could not be read. The figures below are withheld rather than guessed.
        </p>
      </section>
    );
  }

  if (!report) {
    return (
      <section className="card p-4" aria-labelledby="health-heading">
        <h2 id="health-heading" className="text-h2 font-semibold">
          Operational health
        </h2>
        <div className="mt-3 h-8 w-full animate-pulse rounded-card bg-surface" />
        <div className="mt-3 flex gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-7 w-28 animate-pulse rounded-pill bg-surface" />
          ))}
        </div>
      </section>
    );
  }

  const drawn = report.segments.filter((s) => s.count > 0);
  const total = report.totalProjects;

  return (
    <section className="card p-4" aria-labelledby="health-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="health-heading" className="text-h2 font-semibold">
          Operational health
        </h2>
        <p className="text-micro text-text-secondary">
          {total} project{total === 1 ? '' : 's'} · {report.totalAtRisk} flagged ·{' '}
          {report.scope.join(', ') || 'no entity'} · as of{' '}
          {new Date(report.asOf).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
          })}
        </p>
      </div>

      {total === 0 ? (
        <p className="mt-3 text-body text-text-secondary">
          No projects are in this scope yet. The bar appears as soon as one is recorded.
        </p>
      ) : (
        <>
          {/*
            The bar is a single flex row of buttons. Each button is its own
            tap target and carries its own accessible name, so the widget is
            operable from the keyboard without a custom key handler.
          */}
          <div
            className="mt-3 flex h-11 w-full overflow-hidden rounded-card"
            role="group"
            aria-label="Projects by status"
          >
            {drawn.map((segment) => {
              const active = selected === null || selected === segment.status;
              return (
                <button
                  key={segment.status}
                  type="button"
                  onClick={() => onSelect(selected === segment.status ? null : segment.status)}
                  onMouseEnter={() => setHovered(segment.status)}
                  onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered(segment.status)}
                  onBlur={() => setHovered(null)}
                  aria-pressed={selected === segment.status}
                  title={tooltip(segment)}
                  style={{
                    flexGrow: segment.count,
                    flexBasis: 0,
                    // §1.2.1: the pre-filtered segment sits at full saturation
                    // and the rest recessed. The recession is carried by the
                    // fill, not by opacity, so the count stays readable.
                    ...segmentPaint(segment.status, active),
                  }}
                  className="relative min-h-0 min-w-0 border-0 px-1 text-micro font-semibold transition-colors"
                >
                  <span className="numeric block truncate">{segment.count}</span>
                  {segment.atRisk > 0 ? (
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute right-1 top-1 block h-2 w-2 rounded-full bg-danger ring-1 ring-white"
                    />
                  ) : null}
                </button>
              );
            })}
          </div>

          {hovered ? (
            <p role="status" className="mt-2 text-micro text-text-secondary">
              {tooltip(report.segments.find((s) => s.status === hovered)!)}
            </p>
          ) : null}
        </>
      )}

      {/* §1.2.1 chip row: every status, including the empty ones. */}
      <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Filter by status">
        {report.segments.map((segment) => {
          const isSelected = selected === segment.status;
          return (
            <button
              key={segment.status}
              type="button"
              disabled={segment.count === 0}
              onClick={() => onSelect(isSelected ? null : segment.status)}
              aria-pressed={isSelected}
              className={`inline-flex min-h-touch items-center gap-2 rounded-pill border px-3 text-micro font-medium ${
                isSelected
                  ? 'border-transparent bg-royal text-white'
                  : segment.count === 0
                    // A zero chip is still information — it states that no
                    // project sits in that status — so it is muted with a
                    // recessed surface rather than with opacity, which
                    // measured 2.57:1 against white and was unreadable.
                    ? 'cursor-default border-border bg-surface text-text-secondary'
                    : 'border-border bg-white text-text-primary hover:bg-surface'
              }`}
            >
              <span>{statusLabel(segment.status)}</span>
              <span className="numeric">{segment.count}</span>
              {segment.atRisk > 0 ? (
                <span className="inline-flex items-center gap-1 text-danger">
                  <span aria-hidden="true" className="block h-2 w-2 rounded-full bg-danger" />
                  <span className="sr-only">flagged</span>
                  <span className="numeric text-danger">{segment.atRisk}</span>
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/*
        Why every money column on this screen is blank, said once and plainly.
        §1.2.1 asks for aggregate contract value per segment; there is no
        financial record to aggregate yet, and reporting that as $0 would be a
        false measurement.
      */}
      {!report.money.available ? (
        <p className="mt-3 border-t border-border pt-3 text-micro text-text-secondary">
          Contract value is not shown: {report.money.projectsWithFinancials} of{' '}
          {report.money.projectsTotal} projects in scope carry a financial record. Figures appear
          once financials are entered.
        </p>
      ) : null}
      {report.flagsWithoutAColumn.length > 0 ? (
        <p className="mt-1 text-micro text-text-secondary">
          Red-flag counts cover schedule, budget, blocked and compliance. Billing-age flags are not
          yet tracked.
        </p>
      ) : null}
    </section>
  );
}

function tooltip(segment: HealthSegment): string {
  const value = money(segment.contractValue);
  return [
    statusLabel(segment.status),
    `${segment.count} project${segment.count === 1 ? '' : 's'}`,
    value ? `${value} contract value` : 'contract value not recorded',
    `${segment.atRisk} at risk`,
  ].join(' · ');
}
